/**
 * `/breadth/brief/*` — the Daily Brief board's widgets (`services/qv-data/app/qf/daily_brief.py`).
 *
 * One read per kind of widget, all `ind:JAS`. A failure throws and the widget
 * says so; there is nothing to retry that the next 60-second refresh will not.
 */
import { authHeaders } from "../lib/session";
import { httpBase } from "./backend";

const URL_BASE = httpBase(
  import.meta.env.VITE_QV_IND_URL as string | undefined,
  import.meta.env.VITE_QV_SET_URL as string | undefined,
);

/** One bar measured QV's `/dailyperf/` way; every percent is null on a zero denominator. */
export interface PerfNumbers {
  /** The bar's session, `YYYY-MM-DD`. */
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  /** 1D %. */
  chg1: number | null;
  /** Open, high and low against the previous close, %. */
  openPc: number | null;
  highPc: number | null;
  lowPc: number | null;
  /** `(c − o) / o`, %. */
  fromOpen: number | null;
  /** `(h − l) / o`, %. */
  rangePc: number | null;
  /** Where the close sits between low and high, 0–100. */
  closeRange: number | null;
  chg5: number | null;
  chg20: number | null;
  chg50: number | null;
}

export interface PerfRow extends PerfNumbers {
  symbol: string;
  /** The symbol store's name. */
  name: string | null;
}

export interface PerfPayload {
  /** A symbol the server has no bars for is absent. */
  rows: PerfRow[];
  /** True while today's bars move with the 1-minute snapshot. */
  live: boolean;
}

export interface SectorRow extends PerfNumbers {
  sector: string | null;
  /** The sector's SPDR. */
  etf: string | null;
}

export type SectorSource = "equal" | "etf";

export interface SectorsPayload {
  /** Ranked by 1D, best first. */
  rows: SectorRow[];
  source: SectorSource;
  live: boolean;
}

/** `[1D, 2D]`, percent; null where the list had nothing to count. */
export type DayPair = [number | null, number | null];

export interface MarketList {
  date: string;
  stocks: number;
  sectors: number;
  industries: number;
  rows: {
    sectorAD: DayPair;
    industryAD: DayPair;
    advance: DayPair;
    /** A state, so `[today, the session before]`. */
    aboveMid: DayPair;
  };
}

export interface MarketPayload {
  /** `SP1500`, `NYSE`, `NASDAQ`. */
  lists: Record<string, MarketList | null>;
  live: boolean;
}

export interface NhnlWindow {
  /** `1D` … `1M` on Short, `1 Month` … `12 Month` on Long. */
  window: string;
  nh: number;
  nl: number;
}

export interface NhnlList {
  date: string;
  stocks: number;
  short: NhnlWindow[];
  long: NhnlWindow[];
}

export interface NhnlPayload {
  /** `NYSE`, `NASDAQ`. */
  lists: Record<string, NhnlList | null>;
  live: boolean;
}

export type RaceFreq = "w" | "d";

/** One lane: Strength and Momentum against SPY on every period of the window, null on a gap. */
export interface RaceMember {
  symbol: string;
  strength: (number | null)[];
  momentum: (number | null)[];
}

export interface RacePayload {
  /** Every period of the window, oldest first; a weekly date is the week's first day. */
  dates: string[];
  /** A symbol the server has no bars for is absent. */
  members: RaceMember[];
  freq: RaceFreq;
  /** Strength's EMA length and Momentum's lookback, in bars. */
  n: number;
  m: number;
  live: boolean;
}

async function get<T>(path: string, what: string): Promise<T> {
  const res = await fetch(`${URL_BASE.replace(/\/$/, "")}/breadth/brief/${path}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const detail = await res.json().then((b) => b?.detail).catch(() => undefined);
    throw new Error(detail ?? `${what}: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function fetchBriefMarket(): Promise<MarketPayload> {
  return get("market", "Market Performance");
}

export function fetchBriefNhnl(): Promise<NhnlPayload> {
  return get("nhnl", "New Highs / New Lows");
}

export function fetchBriefSectors(source: SectorSource): Promise<SectorsPayload> {
  return get(`sectors?${new URLSearchParams({ source })}`, "Sector Performance");
}

export function fetchBriefPerf(symbols: readonly string[]): Promise<PerfPayload> {
  return get(`perf?${new URLSearchParams({ symbols: symbols.join(",") })}`, "Daily Brief");
}

/**
 * `null` races the 11 SPDR sector funds (Sector Race); a list races those symbols (ETF Performance's Race).
 * An empty list is an empty race and asks nothing — the server reads a missing or empty `symbols` as the sectors.
 */
export function fetchBriefRace(symbols: readonly string[] | null, freq: RaceFreq): Promise<RacePayload> {
  if (symbols !== null && symbols.length === 0) {
    return Promise.resolve({ dates: [], members: [], freq, n: 10, m: 4, live: false });
  }
  const params = new URLSearchParams({ freq });
  if (symbols !== null) params.set("symbols", symbols.join(","));
  return get(`race?${params}`, "Sector Race");
}
